import { Component, type OnDestroy, ViewEncapsulation } from '@angular/core'
import { CptGoogleTagmanagerService, CustomerPortalSharedModule } from '@inside-hub-app/customer-portal-shared'
import { type CustomerPortalConfig } from '@inside-hub-app/customer-portal-config'
import { EfRemoteConfigurationService } from '@inside-hub-app/ef-remote-config'
import { TranslateService } from '@ngx-translate/core'
import { NGXLogger } from 'ngx-logger'
import { DataService } from '../../../services/data.service'
import {
  type ActionPartial, type FooterCardPartial, type LegalNoticePartial, type LogoPartial, type NewsletterPartial, type SocialMediaPartial, type StructureFooterPartial, type ActionLinkPartial, type PagesPartial
} from '../../../models/header-footer.models'
import { SharedService } from '../../../services/shared.service'

import { CommonModule } from '@angular/common'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { HubChildTranslateModule } from '@inside-hub-app/hub-translate'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { NgxGlideModule } from 'ngx-glide'
import { GoogleMapsModule } from '@angular/google-maps'
import { A11yModule } from '@angular/cdk/a11y'
import { TextFieldModule } from '@angular/cdk/text-field'
@Component({
  selector: 'customer-portal-app-footer-v2',
  standalone: true,
  templateUrl: './footer-v2.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    CustomerPortalSharedModule,
    HubChildTranslateModule,
    NgxSkeletonLoaderModule,
    NgxGlideModule,
    GoogleMapsModule,
    A11yModule,
    TextFieldModule
  ]
})
export class FooterV2Component implements OnDestroy {
  public footer: StructureFooterPartial | undefined
  public logo: LogoPartial | undefined
  pages: PagesPartial[] = []
  cols: PagesPartial[][] = []
  public newsletter: NewsletterPartial | undefined
  public socialMedia: SocialMediaPartial[] | undefined
  public legals: ActionPartial[] | undefined
  baseUrl: string
  public legalNotice: LegalNoticePartial | undefined
  public image: LogoPartial | undefined
  public card: FooterCardPartial | undefined
  countryName

  onHeaderFooterDataLoadedSub
  isMobilePwa
  efWebsiteUrl
  constructor (
    private readonly translateService: TranslateService,
    private readonly logger: NGXLogger,
    private readonly dataService: DataService,
    private readonly sharedService: SharedService,
    private readonly cptGtmService: CptGoogleTagmanagerService,
    private readonly remoteConfigService: EfRemoteConfigurationService<CustomerPortalConfig>
  ) {
    this.countryName = this.remoteConfigService.get('country.name')
    this.efWebsiteUrl = this.remoteConfigService.get('efWebsiteUrl')

    this.onHeaderFooterDataLoadedSub =
      this.dataService.onHeaderFooterDataLoaded.subscribe(value => {
        if (value != null) {
          // New V2 structure: data is in structure.footer.content
          this.footer = value.structure?.footer
          const logoData = value.structure?.footer?.content?.header?.logo
          this.logo = Array.isArray(logoData?.[0]) ? logoData[0][0] : logoData?.[0]
          this.pages = value.structure?.footer?.content?.pages ?? []
          this.cols = this.pages[0] && this.splitArrayIntoChunksOfLen(this.pages, 6)
          this.newsletter = value.structure?.footer?.content?.newsletter
          this.socialMedia = value.structure?.footer?.content?.socialmedia
          this.legals = value.structure?.footer?.content?.legal
          this.legalNotice = value.structure?.footer?.content?.legalNotice
          this.image =
            value.structure?.footer?.content?.card?.content?.imageMedium[0][0]
          this.card = value.structure?.footer?.content?.card
        }
      })

    this.baseUrl =
      this.efWebsiteUrl +
      (this.remoteConfigService.get('country.code') === 'ch'
        ? '/' + this.translateService.currentLang
        : '')

    this.isMobilePwa = this.sharedService.isMobilePwa()
  }

  ngOnDestroy (): void {
    try {
      this.onHeaderFooterDataLoadedSub.unsubscribe()
    } catch (error) {
      // no need to show log
    }
  }

  splitArrayIntoChunksOfLen<T> (arr: T[], len: number): T[][] {
    const chunks: T[][] = []
    let i = 0
    const n = arr.length
    while (i < n) {
      chunks.push(arr.slice(i, (i += len)))
    }
    return chunks
  }

  getLinkUrl (link: string | ActionLinkPartial): string {
    return typeof link === 'string' ? link : link?.url || ''
  }

  getHref (page: PagesPartial): string {
    // Handle both string and object link formats
    const link = page?.content?.link
    const url = typeof link === 'string' ? link : link?.url
    if (!url) return '#'
    return url.startsWith('http') ? url : this.baseUrl + url
  }

  sendGTMNavigationData (label?: string): void {
    this.cptGtmService.sendNavigationLinkData(null, 'Footer Navigation', label)
  }
}
